/**
 * EXPECTED PACE, and what it is NOT.
 *
 * Expected pace is a TEACHER INTENTION: the level the facilitator has set as
 * where the class is expected to be. It is not a measurement, it is not a claim
 * about where any squad actually is, and no control built on it may say
 * otherwise.
 *
 * That constraint is the A1/A2 ruling, restated because this is the one module
 * every surface reads it through:
 *   A1: no control may claim a level for the whole room from
 *       `teachers.current_level`. Every per-squad fact comes from that squad's
 *       OWN evaluation capsule.
 *   A2: Coach cards have no squad-level selector. The shared Dashboard command
 *       band may write the room's expected pace only through its guarded control.
 *
 * ONE SELECTOR, ON PURPOSE. The store is `teachers.current_level`, which is
 * acceptable only under the current operating model (one teacher, one cohort,
 * one challenge). Before a teacher can run several cohorts it has to become
 * (cohort, challenge)-scoped. Every consumer reads pace through this function
 * so that migration is a one-point change rather than a grep.
 */

/**
 * @param {object|null} envelope the coach glance envelope (top level, no data wrapper)
 * @returns {null|{
 *   storedLevel: number|null,
 *   resolvedLevel: number|null,
 *   changedAt: string|null,
 *   changedByName: string|null,
 *   source: string|null,
 *   outOfRange: boolean,
 *   resolvedIsHighest: boolean,
 * }}
 *   null when the server carries no expected pace at all, which is a real and
 *   authorized state: a cohort_staff grantee has no `teachers` row, and the
 *   empty-topology response carries `expected_pace: null` too. Callers render
 *   that as "not recorded" and NEVER as a level.
 */
export const selectExpectedPace = (envelope) => {
  const pace = envelope?.expected_pace;
  if (!pace || typeof pace !== "object") return null;

  const storedLevel = positiveIntegerOrNull(pace.stored_level);
  const resolvedLevel = positiveIntegerOrNull(pace.resolved_level_number);

  // The clamp is VISIBLE, never silent. `resolveCapsuleForLevel` clamps a
  // stored level past the end of the challenge down to the last capsule, so a
  // teacher who set level 9 on an 8-level challenge would otherwise see "level
  // 8" with no indication their setting was not what is being shown.
  const outOfRange =
    storedLevel !== null &&
    resolvedLevel !== null &&
    storedLevel !== resolvedLevel;

  return {
    storedLevel,
    resolvedLevel,
    changedAt: stringOrNull(pace.changed_at),
    changedByName: stringOrNull(pace.changed_by_name),
    source: stringOrNull(pace.source),
    outOfRange,
    // Only claim "the highest this challenge has" when the capsule list agrees.
    // Asserting it from the clamp alone would be inventing a fact about the
    // challenge from a fact about one teacher's setting.
    resolvedIsHighest:
      outOfRange &&
      resolvedLevel !== null &&
      resolvedLevel === highestLevel(envelope),
  };
};

/** The highest level_number the resolved challenge actually has, or null. */
export const highestLevel = (envelope) => {
  const capsules = Array.isArray(envelope?.capsules) ? envelope.capsules : [];
  const levels = capsules
    .map((capsule) => positiveIntegerOrNull(capsule?.level_number))
    .filter((level) => level !== null);
  return levels.length ? Math.max(...levels) : null;
};

/**
 * The pace sentence. Assembled here rather than in JSX so the out-of-range
 * wording and the not-recorded wording live next to the rule that produces
 * them, and so a test can pin every branch without rendering.
 *
 * House rules: lowercase, no em dashes, no Title Case.
 */
export const expectedPaceLabel = (pace) => {
  if (!pace || pace.storedLevel === null) return "expected pace: not recorded";

  if (!pace.outOfRange) return `expected pace: level ${pace.storedLevel}`;

  const tail = pace.resolvedIsHighest
    ? `showing level ${pace.resolvedLevel}, the highest this challenge has.`
    : `showing level ${pace.resolvedLevel}.`;
  return `expected pace: level ${pace.storedLevel}. ${tail}`;
};

/**
 * Who moved it and when, rendered whenever the server knows, and rendered
 * honestly when the change did not come from a pace control. `source` values
 * other than `pace_surface` mean the level moved as a side effect of onboarding
 * or a program position write, which is a thing a facilitator should be able to
 * see rather than a thing that looks like they did it themselves.
 */
export const paceProvenanceLabel = (pace) => {
  if (!pace || !pace.changedAt) return "";
  const parts = [];
  if (pace.changedByName) parts.push(`set by ${pace.changedByName}`);
  if (pace.source && pace.source !== "pace_surface") {
    parts.push(SOURCE_COPY[pace.source] || "changed outside the pace control");
  }
  return parts.join(", ");
};

const SOURCE_COPY = {
  onboarding_step: "changed during onboarding",
  program_position: "changed by a program position update",
  onboarding_skip: "changed when onboarding was skipped",
  other: "changed outside the pace control",
};

const positiveIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "number" && typeof value !== "string") return null;
  const asNumber = Number(value);
  return Number.isInteger(asNumber) && asNumber > 0 ? asNumber : null;
};

const stringOrNull = (value) =>
  typeof value === "string" && value.trim() !== "" ? value : null;
